/**
 * Token persistence. The refresh token is the durable credential (survives
 * reloads via localStorage); the short-lived access token is cached in memory
 * and also mirrored to storage so a reload can make authenticated calls
 * immediately without waiting for a refresh round-trip.
 *
 * This is a deliberate SPA trade-off: coaches use trusted devices, and the
 * refresh-token rotation + reuse detection on the API (DEC-0012) contains the
 * blast radius of a stolen token.
 */

const ACCESS_KEY = 'brainkey.accessToken';
const REFRESH_KEY = 'brainkey.refreshToken';

export interface StoredTokens {
  accessToken: string;
  refreshToken: string;
}

let accessToken: string | null = null;

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null; // storage disabled (private mode / SSR) — degrade gracefully
  }
}

function writeStorage(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

export const tokenStore = {
  getAccessToken(): string | null {
    if (accessToken !== null) return accessToken;
    accessToken = readStorage(ACCESS_KEY);
    return accessToken;
  },

  getRefreshToken(): string | null {
    return readStorage(REFRESH_KEY);
  },

  hasSession(): boolean {
    return this.getRefreshToken() !== null;
  },

  set(tokens: StoredTokens): void {
    accessToken = tokens.accessToken;
    writeStorage(ACCESS_KEY, tokens.accessToken);
    writeStorage(REFRESH_KEY, tokens.refreshToken);
  },

  clear(): void {
    accessToken = null;
    writeStorage(ACCESS_KEY, null);
    writeStorage(REFRESH_KEY, null);
  },
};
